import React, { useRef, Suspense, lazy } from 'react';
import isEqual from 'lodash/isEqual';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { CollabEventConflictPayload } from '@atlaskit/editor-common/collab';

import type { ConflictResolutionUIPlugin } from './conflictResolutionUIPluginType';
import { createPlugin } from './pm-plugins/main';
import { conflictResolutionPluginKey } from './pm-plugins/pluginKey';
import { setMetadata, navigateToAvailable } from './pm-plugins/pluginState';

const LazyContentComponent = lazy(async () => ({
	default: (
		await import(
			/* webpackChunkName: "loadable-content-resolution-content-component" */ './ui/contentComponent'
		)
	).ContentComponent,
}));

export const conflictResolutionUIPlugin: ConflictResolutionUIPlugin = ({ api }) => {
	return {
		name: 'conflictResolutionUI',
		contentComponent: ({ editorView }) => {
			return (
				<Suspense fallback={null}>
					<LazyContentComponent api={api} editorView={editorView} />
				</Suspense>
			);
		},
		getSharedState: (state) => {
			if (!state) {
				return undefined;
			}
			return {
				selected: conflictResolutionPluginKey.getState(state)?.selected,
				isNextEnabled: conflictResolutionPluginKey.getState(state)?.isNextEnabled ?? true,
				isPreviousEnabled: conflictResolutionPluginKey.getState(state)?.isPreviousEnabled ?? true,
				conflicts: conflictResolutionPluginKey.getState(state)?.conflicts,
			};
		},
		commands: {
			navigateToFirstConflict: ({ tr }) => {
				const conflicts = api?.conflictResolutionUI?.sharedState.currentState()?.conflicts;

				if (conflicts && (conflicts.inserted.length > 0 || conflicts?.deleted.length > 0)) {
					return navigateToAvailable(undefined, tr, conflicts);
				}
				return null;
			},
		},
		pmPlugins: () => {
			return [
				{
					name: 'conflictResolutionPlugin',
					plugin: createPlugin,
				},
			];
		},
		usePluginHook: ({ editorView }) => {
			const lastReconnectionConflictMetadataRef = useRef<CollabEventConflictPayload | undefined>();

			usePluginStateEffect(api, ['collabEdit'], ({ collabEditState }) => {
				const { lastReconnectionConflictMetadata } = collabEditState ?? {};
				if (
					lastReconnectionConflictMetadata &&
					!isEqual(lastReconnectionConflictMetadataRef.current, lastReconnectionConflictMetadata)
				) {
					setMetadata(
						editorView,
						{
							type: 'update',
							data: lastReconnectionConflictMetadata,
						},
						{ api },
					);
					lastReconnectionConflictMetadataRef.current = lastReconnectionConflictMetadata;
				}
			});
		},
	};
};
